// Duyurular — Yönetim/Plan ekranı için saf yardımcılar (components/announcements).
// Kurallar backend'de (services/announcements.py) doğrulanır; buradaki kontroller
// sadece kullanıcıya erken ve yerinde mesaj göstermek için.

export const AUDIENCE_ROLES = ["operator", "plan", "depo", "boyaci", "sofor", "yonetim"];

export const ROLE_LABELS = {
  operator: "Operatör",
  plan: "Plan",
  depo: "Depo",
  boyaci: "Boyacı",
  sofor: "Şoför",
  yonetim: "Yönetim",
};

export const TITLE_MAX = 120;
export const BODY_MAX = 2000;
export const DEFAULT_EXPIRY_DAYS = 7;
export const MAX_EXPIRY_DAYS = 90;

export const STATUS = {
  active: { label: "Yayında", tone: "success" },
  expired: { label: "Süresi doldu", tone: "muted" },
  deleted: { label: "Silindi", tone: "error" },
};

export const READER_STATUS = {
  unseen: { label: "Görmedi", tone: "error" },
  seen: { label: "Gördü", tone: "info" },
  acked: { label: "Onayladı", tone: "success" },
};

export const DELIVERY_KIND = {
  publish: "Yayın bildirimi",
  update: "Güncelleme bildirimi",
  audience_add: "Yeni hedeflere bildirim",
  resend: "Görmeyenlere tekrar bildirim",
};

// "YYYY-AA-GG" + gün → "YYYY-AA-GG" (saat diliminden bağımsız takvim aritmetiği)
export function addDays(day, n) {
  const [y, m, d] = day.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

export function audienceLabel(audience) {
  if (!audience || audience.all) return "Tüm personel";
  return (audience.roles || []).map((r) => ROLE_LABELS[r] || r).join(", ");
}

const IST_DT = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
});
const IST_DAY = new Intl.DateTimeFormat("tr-TR", {
  timeZone: "Europe/Istanbul", day: "numeric", month: "long", year: "numeric",
});

// Sunucu ISO zamanı → "29 Eyl 14:05" (İstanbul)
export function formatDateTime(iso) {
  return iso ? IST_DT.format(new Date(iso)) : "";
}

// "YYYY-AA-GG" → "6 Ekim 2026" (gün öğlen alınır → saat dilimi kaymaz)
export function formatDay(day) {
  return day ? IST_DAY.format(new Date(`${day}T12:00:00Z`)) : "";
}

export function emptyDraft(today) {
  return {
    title: "", body: "", severity: "normal",
    audience: { all: true, roles: [] },
    expires_on: addDays(today, DEFAULT_EXPIRY_DAYS),
  };
}

export function draftFrom(doc) {
  return {
    title: doc.title, body: doc.body, severity: doc.severity,
    audience: { all: !!doc.audience?.all, roles: [...(doc.audience?.roles || [])] },
    expires_on: doc.expires_on,
  };
}

// Rol işaretle/kaldır; sıra backend'deki gibi AUDIENCE_ROLES sırası
export function toggleRole(audience, role) {
  const set = new Set(audience.roles || []);
  if (set.has(role)) set.delete(role); else set.add(role);
  return { all: false, roles: AUDIENCE_ROLES.filter((r) => set.has(r)) };
}

// İlk hata mesajı veya null
export function validateDraft(draft, today) {
  const title = draft.title.trim();
  const body = draft.body.trim();
  if (!title) return "Başlık yazın";
  if (title.length > TITLE_MAX) return `Başlık en fazla ${TITLE_MAX} karakter olabilir`;
  if (!body) return "Duyuru metnini yazın";
  if (body.length > BODY_MAX) return `Metin en fazla ${BODY_MAX} karakter olabilir`;
  if (!draft.audience.all && !(draft.audience.roles || []).length) return "Hedef kitle seçin: Tüm personel veya en az bir rol";
  if (!draft.expires_on) return "Bitiş tarihi seçin";
  if (draft.expires_on < today) return "Bitiş tarihi geçmişte olamaz";
  if (draft.expires_on > addDays(today, MAX_EXPIRY_DAYS)) return `Bitiş tarihi en fazla ${MAX_EXPIRY_DAYS} gün sonrası olabilir`;
  return null;
}

const sameAudience = (a, b) => !!a.all === !!b.all && JSON.stringify(a.all ? [] : a.roles) === JSON.stringify(b.all ? [] : b.roles);

// Kaydedilmemiş değişiklik var mı (form kapatılırken onay için)
export function isDraftDirty(draft, base) {
  return draft.title !== base.title || draft.body !== base.body || draft.severity !== base.severity
    || draft.expires_on !== base.expires_on || !sameAudience(draft.audience, base.audience);
}

export function textChanged(draft, doc) {
  return draft.title.trim() !== doc.title || draft.body.trim() !== doc.body;
}

// Düzenlemenin sonucu (backend kuralıyla aynı):
// - normal → önemli: her zaman içerik değişikliği (onay istenir, herkese bildirim)
// - metin değişti + "content": sürüm artar; önemlide onaylar sıfırlanır + bildirim,
//   normalde bildirim sadece notify seçiliyse
// - hedefe rol eklendi (içerik değişikliği yoksa): sadece yeni hedeflere bildirim
export function editOutcome(draft, doc, changeType, notify) {
  const becameImportant = doc.severity === "normal" && draft.severity === "important";
  const content = becameImportant || (textChanged(draft, doc) && changeType === "content");
  const important = draft.severity === "important";
  const audienceChanged = !sameAudience(draft.audience, doc.audience);
  return {
    becameImportant,
    content,
    resetsAcks: content && important,
    notifyAll: content && (important || notify),
    notifyAdded: !(content && (important || notify)) && audienceChanged,
  };
}

// Oluşturma gövdesi
export function createPayload(draft) {
  return {
    title: draft.title.trim(), body: draft.body.trim(), severity: draft.severity,
    audience: draft.audience.all ? { all: true } : { all: false, roles: draft.audience.roles },
    expires_on: draft.expires_on,
  };
}

// Düzenleme gövdesi: sadece değişen alanlar + rev (iyimser kilit) + change_type/notify
export function updatePayload(draft, doc, changeType, notify) {
  const full = createPayload(draft);
  const out = { rev: doc.rev };
  if (full.title !== doc.title) out.title = full.title;
  if (full.body !== doc.body) out.body = full.body;
  if (full.severity !== doc.severity) out.severity = full.severity;
  if (!sameAudience(draft.audience, doc.audience)) out.audience = full.audience;
  if (full.expires_on !== doc.expires_on) out.expires_on = full.expires_on;
  if (textChanged(draft, doc)) out.change_type = changeType;
  if (notify && changeType === "content" && draft.severity === "normal") out.notify = true;
  return out;
}

export const defaultChangeType = (severity) => (severity === "important" ? "content" : "minor");

// Tekrar bildir düğmesi: görmeyen yoksa / aktif değilse / saatlik sınır dolmadıysa kapalı
export function resendState(doc, unseen, nowMs = Date.now()) {
  if (doc.status !== "active") return { ok: false, reason: "Duyuru yayında değil" };
  if (!unseen) return { ok: false, reason: "Hedefteki herkes gördü" };
  if (doc.last_resend_at) {
    const next = new Date(doc.last_resend_at).getTime() + 60 * 60 * 1000;
    if (next > nowMs) return { ok: false, reason: `Sonraki tekrar bildirim: ${formatDateTime(new Date(next).toISOString())}` };
  }
  return { ok: true, reason: "" };
}

// ─── Personel tarafı (ana sayfa kartı, şerit, tablet) ───────────────────────

export const FEED_REFRESH_MS = 2 * 60 * 1000;
// Akış isteği başarısız olursa tekrar deneme aralıkları (sonuncusu tekrar eder)
export const FEED_RETRY_MS = [3000, 10000, 30000];
// Onay/görme sonrası diğer bileşenler (kart ↔ şerit) yenilensin
export const FEED_CHANGED_EVENT = "announcements-changed";

// Şerit sadece panellerde: ana sayfa (kart zaten gösteriyor), Canlı Pano, sipariş
// takip / müşteri takip linki ve tablet hariç
const STRIP_PATHS = ["/operator", "/plan", "/management", "/warehouse", "/paint", "/driver", "/bobin",
  "/marka-stok", "/boyaci", "/kamera", "/vardiya-plani"];
export function stripAllowedOn(pathname) {
  return STRIP_PATHS.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

export const needsAck = (a) => a.severity === "important" && !a.me?.acked;

// Onay bekleyen önemliler (feed zaten önemliler önce, en yeni üstte sıralı)
export const pendingImportant = (items) => (items || []).filter(needsAck);

// Ana sayfa kartı: ilk `max` duyuru + kalan sayısı
export function cardSlice(items, max, expanded) {
  const all = items || [];
  if (expanded || all.length <= max) return { shown: all, rest: 0 };
  return { shown: all.slice(0, max), rest: all.length - max };
}
